import axios from 'axios';
import type { User } from '../types/User';

const BASE_URL = process.env.REACT_APP_API_URL;

interface PaginatedResponse<T> {
  users: T[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

interface UserFilters {
  search?: string;
  roleId?: number;
  status?: string;
  hasPortalAccess?: boolean;
  serviceAreaId?: number;
  foremanAreaId?: number;
  /** Server-side sort (allowlisted): name, lastName, employeeNumber, email, status, activeSeason, hasPortalAccess, role. */
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

interface ServiceAreaUserFilters {
  search?: string;
  status?: string;
}

export const usersService = {
  getUsers: async (page: number = 1, limit: number = 25, filters?: UserFilters, skipCache?: boolean): Promise<PaginatedResponse<User>> => {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString()
    });

    // Add filter parameters if they exist
    if (filters?.search && filters.search.trim()) {
      params.append('search', filters.search.trim());
    }
    if (filters?.roleId) {
      params.append('roleId', filters.roleId.toString());
    }
    if (filters?.status && filters.status.trim()) {
      params.append('status', filters.status.trim());
    }
    if (filters?.hasPortalAccess !== undefined) {
      params.append('hasPortalAccess', filters.hasPortalAccess.toString());
    }
    if (filters?.serviceAreaId) {
      params.append('serviceAreaId', filters.serviceAreaId.toString());
    }
    if (filters?.foremanAreaId) {
      params.append('foremanAreaId', filters.foremanAreaId.toString());
    }
    if (filters?.sortBy) {
      params.append('sortBy', filters.sortBy);
    }
    if (filters?.sortOrder) {
      params.append('sortOrder', filters.sortOrder);
    }

    // Add noCache parameter if skipCache is true
    if (skipCache) {
      params.append('noCache', 'true');
    }

    const response = await axios.get(`${BASE_URL}/users?${params.toString()}`);
    return response.data;
  },

  getUser: async (id: number): Promise<User> => {
    const response = await axios.get(`${BASE_URL}/users/${id}`);
    return response.data;
  },

  getUsersByServiceArea: async (
    serviceAreaId: number, 
    page: number = 1, 
    limit: number = 100, 
    filters?: ServiceAreaUserFilters,
    skipCache?: boolean
  ): Promise<PaginatedResponse<User>> => {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString()
    });

    // Add filter parameters if they exist
    if (filters?.search && filters.search.trim()) {
      params.append('search', filters.search.trim());
    }
    if (filters?.status && filters.status.trim()) {
      params.append('status', filters.status.trim());
    }
    
    // Add noCache parameter if skipCache is true
    if (skipCache) {
      params.append('noCache', 'true');
    }

    const response = await axios.get(`${BASE_URL}/users/service-area/${serviceAreaId}?${params.toString()}`);
    return response.data;
  },

  createUser: async (userData: Partial<User>): Promise<User> => {
    const response = await axios.post(`${BASE_URL}/users`, {
      ...userData,
      roleId: Number(userData.roleId)
    });
    return response.data;
  },

  updateUser: async (id: number, userData: Partial<User>): Promise<User> => {
    const payload: Record<string, unknown> = { ...userData };
    if (userData.roleId !== undefined) {
      payload.roleId = userData.roleId != null ? Number(userData.roleId) : null;
    }
    const response = await axios.put(`${BASE_URL}/users/${id}`, payload);
    return response.data;
  },

  deleteUser: async (id: number): Promise<void> => {
    await axios.delete(`${BASE_URL}/users/${id}`);
  },

  getUserHistory: async (id: number): Promise<any[]> => {
    const response = await axios.get(`${BASE_URL}/users/${id}/history`);
    return response.data;
  },

  updatePortalAccess: async (id: number, hasPortalAccess: boolean): Promise<User> => {
    const response = await axios.put(`${BASE_URL}/users/${id}/portal-access`, {
      hasPortalAccess
    });
    return response.data;
  },

  updatePortalInfo: async (id: number, portalData: { portalEmail?: string; portalRoleId?: number; foremanAreaIds?: number[]; defaultForemanAreaId?: number | null }): Promise<User> => {
    const response = await axios.put(`${BASE_URL}/users/${id}/portal-info`, portalData);
    return response.data;
  },

  updateUserSeason: async (id: number, season: 'summer' | 'winter'): Promise<User> => {
    const response = await axios.put(`${BASE_URL}/users/${id}/season`, { activeSeason: season });
    return response.data;
  },

  bulkUpdateSeason: async (userIds: number[] | null, season: 'summer' | 'winter', filters?: UserFilters): Promise<{ count: number }> => {
    const response = await axios.put(`${BASE_URL}/users/bulk-season`, { 
      userIds, 
      activeSeason: season,
      filters
    });
    return response.data;
  }
}; 
